import { MermaidDiagram } from '../../components/developers/MermaidDiagram';

export default function Architecture() {
  const systemDiagram = `graph TB
    subgraph Client Layer
        SDK[TypeScript SDK]
        CLI[CLI Tool - opairctl]
        UI[Console Web UI]
        OAI[OpenAI-Compatible Adapter]
    end

    subgraph API Layer
        GW[API Gateway]
        CP[Control Plane API]
    end

    subgraph Core Services
        PE[Policy Engine]
        DF[Detector Framework]
        BE[Bundle Engine]
        AE[Audit Engine]
        WE[Workflow Engine]
        CR[Cost & Recommendation Engine]
    end

    subgraph Integration Services
        ES[Email Service - SMTP]
        NS[Notification Service]
        WH[Webhook Dispatcher]
        SI[SIEM Connector]
        EC[Ecosystem Connectors]
    end

    subgraph Data Layer
        PG[(PostgreSQL)]
        RD[(Redis Cache)]
        S3[(Object Storage)]
    end

    SDK --> GW
    CLI --> GW
    UI --> GW
    OAI --> GW
    GW --> CP
    CP --> PE
    CP --> DF
    CP --> BE
    CP --> AE
    CP --> WE
    CP --> CR
    CP --> ES
    CP --> NS
    CP --> WH
    CP --> SI
    CP --> EC
    PE --> PG
    PE --> RD
    BE --> S3
    AE --> PG
    WE --> PG
    CR --> PG`;

  const gatewayDiagram = `sequenceDiagram
    participant App as Application
    participant GW as Opair Gateway
    participant DF as Detector Framework
    participant PE as Policy Engine
    participant LLM as LLM Provider

    App->>GW: OpenAI-compatible request
    GW->>GW: Authenticate (JWT/PAT/API Key)
    GW->>DF: Pre-scan input
    DF->>DF: Run detectors (PII, toxicity, injection...)
    DF-->>GW: Detection results
    GW->>PE: Evaluate policies
    PE-->>GW: Allow / Deny / Mask
    alt Request Allowed
        GW->>LLM: Forward to provider
        LLM-->>GW: LLM response
        GW->>DF: Post-scan output
        DF-->>GW: Detection results
        GW-->>App: Governed response
    else Request Denied
        GW-->>App: Policy violation error
    end
    GW->>GW: Emit audit event`;

  const detectorDiagram = `graph LR
    subgraph Input
        TXT[Text Content]
    end

    subgraph Detector Framework
        PII[PII Detector]
        TOX[Toxicity Detector]
        BIAS[Bias Detector]
        PI[Prompt Injection Detector]
        IP[IP Leakage Detector]
        RAG[RAG Poisoning Detector]
    end

    subgraph PII Sub-Detectors
        EMAIL[Email]
        PHONE[Phone Number]
        SSN[SSN / National ID]
        CC[Credit Card]
        ADDR[Address]
    end

    subgraph IP Sub-Detectors
        CODE[Source Code]
        PROP[Proprietary Data]
        SECRETS[API Keys & Secrets]
    end

    TXT --> PII
    TXT --> TOX
    TXT --> BIAS
    TXT --> PI
    TXT --> IP
    TXT --> RAG
    PII --> EMAIL
    PII --> PHONE
    PII --> SSN
    PII --> CC
    PII --> ADDR
    IP --> CODE
    IP --> PROP
    IP --> SECRETS`;

  const workflowDiagram = `sequenceDiagram
    participant User
    participant API as Control Plane API
    participant WE as Workflow Engine
    participant DB as Database

    User->>API: POST /v1/workflows (create)
    API->>DB: Store workflow definition
    API-->>User: Workflow created

    User->>API: POST /v1/workflows/:id/trigger
    API->>WE: Start execution
    WE->>WE: Execute step 1
    WE->>WE: Execute step 2
    WE->>WE: Execute step N
    WE->>DB: Store execution result
    WE-->>API: Execution complete
    API-->>User: Execution ID + status

    User->>API: GET /v1/workflows/:id/executions/:execId
    API->>DB: Fetch execution
    API-->>User: Step-by-step results`;

  const dataFlowDiagram = `sequenceDiagram
    participant Client
    participant Gateway
    participant ControlPlane
    participant PolicyEngine
    participant Database

    Client->>Gateway: Policy Evaluation Request
    Gateway->>ControlPlane: Validate & Route
    ControlPlane->>PolicyEngine: Execute Policy
    PolicyEngine->>Database: Fetch Policy Rules
    Database-->>PolicyEngine: Policy Data
    PolicyEngine-->>ControlPlane: Decision Result
    ControlPlane-->>Gateway: Format Response
    Gateway-->>Client: Policy Decision`;

  const deploymentDiagram = `graph LR
    subgraph Production
        LB[Load Balancer]
        subgraph K8s Cluster
            GW1[Gateway Pod 1]
            GW2[Gateway Pod 2]
            CP1[Control Plane 1]
            CP2[Control Plane 2]
        end
        PG[(PostgreSQL)]
        RD[(Redis)]
    end

    LB --> GW1
    LB --> GW2
    GW1 --> CP1
    GW2 --> CP2
    CP1 --> PG
    CP2 --> PG
    CP1 --> RD
    CP2 --> RD`;

  return (
    <article className="prose prose-slate max-w-none">
      <h1>Architecture</h1>
      <p className="lead">Understand the Opair platform architecture, components, and data flow.</p>

      <h2>System Overview</h2>
      <p>
        Opair follows a microservices architecture designed for scalability, reliability, and
        security. The platform consists of multiple layers with specialized engines for policy
        evaluation, content detection, workflow execution, and cost optimization.
      </p>

      <MermaidDiagram chart={systemDiagram} caption="Opair System Architecture" />

      <h2>Components</h2>

      <h3>Client Layer</h3>
      <ul>
        <li>
          <strong>TypeScript SDK</strong> — Official client library for programmatic access
        </li>
        <li>
          <strong>CLI Tool (opairctl)</strong> — Command-line interface for policy management,
          deployment, and config portability
        </li>
        <li>
          <strong>Console Web UI</strong> — Web-based management dashboard
        </li>
        <li>
          <strong>OpenAI-Compatible Adapter</strong> — Drop-in replacement for OpenAI SDK that
          routes through Opair Gateway
        </li>
      </ul>

      <h3>API Layer</h3>
      <ul>
        <li>
          <strong>API Gateway</strong> — Request routing, authentication (JWT/PAT/API Key), rate
          limiting, and content scanning
        </li>
        <li>
          <strong>Control Plane API</strong> — Core business logic, CRUD operations, and
          orchestration (Fastify)
        </li>
      </ul>

      <h3>Core Services</h3>
      <ul>
        <li>
          <strong>Policy Engine</strong> — Policy evaluation and decision making with version
          control
        </li>
        <li>
          <strong>Detector Framework</strong> — Multi-category content analysis (PII, toxicity,
          bias, prompt injection, IP leakage, RAG poisoning)
        </li>
        <li>
          <strong>Bundle Engine</strong> — Policy bundle compilation, distribution, and rollback
        </li>
        <li>
          <strong>Audit Engine</strong> — Audit logging, compliance reporting, and GDPR data export
        </li>
        <li>
          <strong>Workflow Engine</strong> — Automated workflow execution with step-by-step tracking
        </li>
        <li>
          <strong>Cost &amp; Recommendation Engine</strong> — Usage analytics, budget tracking, and
          cost optimization recommendations
        </li>
      </ul>

      <h3>Integration Services</h3>
      <ul>
        <li>
          <strong>Email Service</strong> — SMTP-based notifications (invitations, alerts, password
          resets)
        </li>
        <li>
          <strong>Notification Service</strong> — In-app notifications with preferences and read
          tracking
        </li>
        <li>
          <strong>Webhook Dispatcher</strong> — Event-driven webhooks with delivery tracking and
          retry
        </li>
        <li>
          <strong>SIEM Connector</strong> — Forward audit events to Splunk, Datadog, Elastic, etc.
        </li>
        <li>
          <strong>Ecosystem Connectors</strong> — Integrations with Jira, Slack, Teams, ServiceNow,
          PagerDuty
        </li>
      </ul>

      <h3>Data Layer</h3>
      <ul>
        <li>
          <strong>PostgreSQL</strong> — Primary data store for policies, users, audit logs,
          workflows
        </li>
        <li>
          <strong>Redis</strong> — Caching layer for performance optimization
        </li>
        <li>
          <strong>Object Storage</strong> — Bundle storage and artifact management
        </li>
      </ul>

      <h2>Gateway Proxy Architecture</h2>
      <p>
        The Opair Gateway acts as an OpenAI-compatible proxy. Applications make standard LLM API
        calls, and the Gateway transparently intercepts, scans, enforces policies, and routes to the
        configured provider.
      </p>

      <MermaidDiagram chart={gatewayDiagram} caption="Gateway Proxy Flow" />

      <h2>Detector Framework</h2>
      <p>
        The policy engine includes a modular detector framework with six categories of content
        analysis, each with specialized sub-detectors:
      </p>

      <MermaidDiagram chart={detectorDiagram} caption="Detector Framework Architecture" />

      <table>
        <thead>
          <tr>
            <th>Detector</th>
            <th>Description</th>
            <th>Key Sub-types</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>PII</strong>
            </td>
            <td>Personally identifiable information</td>
            <td>Email, phone, SSN, credit card, address, name</td>
          </tr>
          <tr>
            <td>
              <strong>Toxicity</strong>
            </td>
            <td>Harmful or offensive content</td>
            <td>Hate speech, threats, harassment, profanity</td>
          </tr>
          <tr>
            <td>
              <strong>Bias</strong>
            </td>
            <td>Discriminatory language or stereotypes</td>
            <td>Gender, racial, age, disability bias</td>
          </tr>
          <tr>
            <td>
              <strong>Prompt Injection</strong>
            </td>
            <td>Adversarial prompt manipulation</td>
            <td>System prompt override, jailbreak, role hijacking</td>
          </tr>
          <tr>
            <td>
              <strong>IP Leakage</strong>
            </td>
            <td>Intellectual property exposure</td>
            <td>Source code, API keys/secrets, proprietary data, patents</td>
          </tr>
          <tr>
            <td>
              <strong>RAG Poisoning</strong>
            </td>
            <td>Retrieval-augmented generation attacks</td>
            <td>Injected context, authority manipulation</td>
          </tr>
        </tbody>
      </table>

      <h2>Workflow Execution Engine</h2>
      <p>
        The workflow engine supports automated multi-step processes with validation, triggering, and
        detailed execution tracking:
      </p>

      <MermaidDiagram chart={workflowDiagram} caption="Workflow Execution Flow" />

      <h2>Data Flow</h2>
      <p>The following diagram shows the typical request flow for a policy evaluation:</p>

      <MermaidDiagram chart={dataFlowDiagram} caption="Policy Evaluation Data Flow" />

      <h2>Deployment Architecture</h2>
      <p>Opair is designed to run on Kubernetes with horizontal scaling capabilities:</p>

      <MermaidDiagram chart={deploymentDiagram} caption="Production Deployment" />

      <h2>Key Design Principles</h2>

      <div className="not-prose grid gap-4 md:grid-cols-2 my-6">
        <div className="p-4 bg-white border border-slate-200 rounded-lg">
          <h4 className="font-semibold text-slate-900">Horizontal Scalability</h4>
          <p className="text-sm text-slate-600 mt-1">
            All stateless components can scale horizontally to handle increased load.
          </p>
        </div>
        <div className="p-4 bg-white border border-slate-200 rounded-lg">
          <h4 className="font-semibold text-slate-900">High Availability</h4>
          <p className="text-sm text-slate-600 mt-1">
            Multi-zone deployment with automatic failover and health checks.
          </p>
        </div>
        <div className="p-4 bg-white border border-slate-200 rounded-lg">
          <h4 className="font-semibold text-slate-900">Security First</h4>
          <p className="text-sm text-slate-600 mt-1">
            End-to-end encryption, RBAC, MFA enforcement, PAT auth, and comprehensive audit logging.
          </p>
        </div>
        <div className="p-4 bg-white border border-slate-200 rounded-lg">
          <h4 className="font-semibold text-slate-900">Low Latency</h4>
          <p className="text-sm text-slate-600 mt-1">
            Optimized for sub-50ms policy evaluation with caching layers.
          </p>
        </div>
      </div>

      <h2>Technology Stack</h2>
      <table>
        <thead>
          <tr>
            <th>Component</th>
            <th>Technology</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Runtime</td>
            <td>Node.js 20 LTS</td>
          </tr>
          <tr>
            <td>Framework</td>
            <td>Fastify</td>
          </tr>
          <tr>
            <td>Database</td>
            <td>PostgreSQL 16</td>
          </tr>
          <tr>
            <td>ORM</td>
            <td>Drizzle ORM</td>
          </tr>
          <tr>
            <td>Cache</td>
            <td>Redis 7</td>
          </tr>
          <tr>
            <td>Frontend</td>
            <td>React 18 + Vite</td>
          </tr>
          <tr>
            <td>Container</td>
            <td>Docker / Kubernetes</td>
          </tr>
          <tr>
            <td>Auth</td>
            <td>JWT + TOTP MFA + PAT + SSO (SAML/OIDC)</td>
          </tr>
          <tr>
            <td>Observability</td>
            <td>Pino (structured logging) + OpenTelemetry tracing</td>
          </tr>
        </tbody>
      </table>
    </article>
  );
}
